import { draftReturnAtomFamily } from "@babackapp/return-widget-components/atoms/draft-return.atom";
import { useCreateReturnRequest } from "@babackapp/return-widget-components/mutations/use-create-return-request";
import { useDraftReturn } from "@babackapp/return-widget-components/hooks/use-draft-return";
import { useFinancialSummary } from "@babackapp/return-widget-components/hooks/use-financial-summary";
import { hasIncompleteLineItems } from "@babackapp/return-widget-components/lib/draft.lib";
import { usePublicOrder } from "@babackapp/return-widget-components/queries/use-public-order";
import type { ReturnRequest } from "@babackapp/return-widget-components/types";
import { useSetAtom } from "jotai";
import { useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { postToParent } from "../lib/shop-now-embed";
import { trackWidgetEvent } from "../lib/track-widget-event";
import { useWidgetParams } from "./use-widget-params";

export type CompleteStatus = "idle" | "pending" | "success" | "error";

export type UseCompleteReturn = {
  status: CompleteStatus;
  returnRequest: ReturnRequest | undefined;
  error: Error | null;
  errorCode: string | null;
  confirm: () => void;
};

export function useComplete(): UseCompleteReturn {
  const { orderId, shopId, shopSlug } = useWidgetParams();
  const navigate = useNavigate();
  const draft = useDraftReturn(orderId);
  const setDraft = useSetAtom(draftReturnAtomFamily(orderId));
  const mutation = useCreateReturnRequest(orderId, shopSlug);
  const { data: order } = usePublicOrder(orderId);
  const financialSummary = useFinancialSummary(orderId, order ?? null);
  // BBS-237: mutation.status only reflects "pending" once React re-renders
  // with the mutate() call's synchronous status update — two clicks in the
  // same tick can both read the pre-update "idle" status and both submit
  // (same race class fixed for use-stripe-checkout.ts's onSubmit).
  const isSubmittingRef = useRef(false);

  const confirm = useCallback(() => {
    if (!draft || mutation.status === "pending" || isSubmittingRef.current)
      return;
    isSubmittingRef.current = true;

    // BBS-237: last-line defense — a real complement is due but no Stripe
    // payment was ever captured for this draft. Refuse to submit rather
    // than trust whatever upstream state (financialSummary, needsPayment)
    // let the customer reach this screen; the backend rejects this too,
    // but failing fast here avoids a confusing round-trip and error toast.
    if (financialSummary.amountDue > 102 && !draft.paymentIntentId) {
      isSubmittingRef.current = false;
      trackWidgetEvent({
        shopId,
        orderId,
        eventType: "payment_failed",
        payload: {
          reason: "complete_guard",
          message: "amountDue due with no paymentIntentId at confirm time",
        },
      });
      navigate("../checkout");
      return;
    }

    // BBS-249: last-line defense — a line item without resolution/reasonId
    // should never reach POST /public/return-request (see incident on
    // return d0Z6vB10FUxtyEk5J1). The summary step already blocks this
    // earlier in the flow; this only catches something that slipped past it
    // (e.g. a future regression in that guard). Only checks line items, not
    // returnMode/address: the no-physical-return path reaches this screen
    // without ever setting either, by design.
    if (hasIncompleteLineItems(draft)) {
      isSubmittingRef.current = false;
      trackWidgetEvent({
        shopId,
        orderId,
        eventType: "line_item_incomplete",
        payload: { reason: "complete_guard" },
      });
      navigate("../summary");
      return;
    }

    mutation.mutate(draft, {
      onError: (mutationError) => {
        isSubmittingRef.current = false;
        // Payment can succeed at Stripe while the backend still refuses to
        // create the return request (e.g. it disagrees the complement was
        // actually captured, or a totally unrelated validation error) — the
        // customer sees an error screen, but until now nothing recorded
        // WHY, only that it failed. This is the exact "paid but no RR"
        // signal this whole fix exists to catch — log everything that
        // could help distinguish a payment-gap rejection from any other
        // failure without needing a DB lookup after the fact.
        const rejectionErrorCode =
          (mutationError as { code?: string })?.code ?? "UNKNOWN";
        trackWidgetEvent({
          shopId,
          orderId,
          eventType: "payment_failed",
          payload: {
            reason: "rr_creation_rejected",
            errorCode: rejectionErrorCode,
            message: mutationError?.message,
            hasIntentId: !!draft.paymentIntentId,
            paymentIntentId: draft.paymentIntentId ?? null,
            amountDue: financialSummary.amountDue,
            needsPayment: financialSummary.amountDue > 102,
          },
        });
      },
      onSuccess: (data) => {
        // BBS-237 follow-up: the key signal — did we submit with an amount
        // still due but no captured payment? Backend flags this too
        // (FlagComplementPaymentMissingAtCreationService), but seeing it here
        // as well pinpoints exactly which widget step let it through. Fired
        // only on actual success — a failed mutation created no ReturnRequest,
        // so recording it here too would misrepresent the signal.
        trackWidgetEvent({
          shopId,
          orderId,
          eventType: "return_request_submitted",
          payload: {
            hasIntentId: !!draft.paymentIntentId,
            amountDue: financialSummary.amountDue,
          },
        });
        setDraft(null);
        postToParent({ type: "CLEAR_SHOPIFY_CART" });
        navigate("../return-requests", { replace: true });
        navigate(`../return-requests/${data.id}`);
      },
    });
  }, [
    draft,
    mutation,
    setDraft,
    navigate,
    shopId,
    orderId,
    financialSummary.amountDue,
  ]);

  const errorCode = mutation.error
    ? ((mutation.error as { code?: string }).code ?? "UNKNOWN")
    : null;

  return {
    status: mutation.status === "idle" ? "idle" : mutation.status,
    returnRequest: mutation.data,
    error: mutation.error,
    errorCode,
    confirm,
  };
}
